import { useState, useEffect } from "react";
import { Loader2, Trash2, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion"; // Custom modal animations ke liye

const AdminVolunteerList = () => {
  const [volunteers, setVolunteers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Custom Delete Modal States
  const [volunteerToDelete, setVolunteerToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // const backendUrl = "http://localhost:1415"; // Apne hisaab se port change karein
  // const backendUrl = "http://localhost/seva-backend"; // Apne hisaab se port change karein
  const backendUrl = "https://www.sevaafoundation.com/seva-backend"; // Apne hisaab se port change karein
  const token = localStorage.getItem("sevaa_admin_token");

  const fetchVolunteers = async () => {
    try {
     const res = await fetch(`${backendUrl}/api/volunteer/all.php`, {
        headers: { "Authorization": `Bearer ${token}` } // Ye header add kiya
      });
      const data = await res.json();
      if (data.success) {
        setVolunteers(data.volunteers);
      }
    } catch (error) {
      console.error("Failed to fetch volunteers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVolunteers();
  }, []);

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
  const res = await fetch(`${backendUrl}/api/volunteer/update_status.php?id=${id}`, {
        method: "PUT",
        headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}` // Token header add kiya
        },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        // UI locally update karein
        setVolunteers(prev => 
            prev.map(v => v._id === id ? { ...v, status: newStatus } : v)
        );
      }
    } catch (error) {
      console.error("Failed to update status");
    }
  };

  // --- NAYA FUNCTION: Custom Confirm Delete ---
  const confirmDelete = async () => {
    if (!volunteerToDelete) return;
    
    setIsDeleting(true);
    try {
     const res = await fetch(`${backendUrl}/api/volunteer/delete.php?id=${volunteerToDelete}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` } // Ye header add kiya
      });
      const data = await res.json();
      if (data.success) {
        setVolunteers(prev => prev.filter(v => v._id !== volunteerToDelete));
        setVolunteerToDelete(null); // Delete hone ke baad modal close
      }
    } catch (error) {
      console.error("Failed to delete record");
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto text-teal-600" size={32} /></div>;
  }

  return (
    <div className="p-8 relative min-h-screen">
      
      {/* CUSTOM DELETE CONFIRMATION MODAL */}
      <AnimatePresence>
        {volunteerToDelete !== null && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            {/* Background Blur Overlay */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !isDeleting && setVolunteerToDelete(null)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            
            {/* Modal Box */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm text-center border border-slate-100"
            >
              <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-5 border-[6px] border-red-50/50">
                <AlertTriangle size={36} strokeWidth={2.5} />
              </div>
              <h3 className="text-2xl font-black text-slate-900 mb-2">Delete Application?</h3>
              <p className="text-slate-500 text-sm mb-8 font-medium">Are you sure you want to delete this volunteer record? This action cannot be undone.</p>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setVolunteerToDelete(null)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-red-500/30"
                >
                  {isDeleting ? <Loader2 size={18} className="animate-spin" /> : "Yes, Delete"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <h2 className="text-3xl font-bold mb-6 text-slate-800">Volunteer Applications</h2>
      
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-800 uppercase text-xs font-semibold">
              <tr>
                <th className="px-6 py-4">Name & Contact</th>
                <th className="px-6 py-4">Role & Availability</th>
                <th className="px-6 py-4">Date Applied</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {volunteers.map((vol) => (
                <tr key={vol._id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-bold text-slate-800">{vol.fullName}</p>
                    <p className="text-xs">{vol.emailAddress}</p>
                    <p className="text-xs">{vol.phoneNumber}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="bg-teal-100 text-teal-800 px-2 py-1 rounded text-xs font-bold block w-fit mb-1">{vol.interestedRole}</span>
                    <span className="text-xs">Time: {vol.availability}</span>
                  </td>
                  <td className="px-6 py-4 text-xs font-medium">
                    {new Date(vol.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      vol.status === 'Approved' ? 'bg-green-100 text-green-700' :
                      vol.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {vol.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right flex items-center justify-end gap-2">
                    {vol.status === 'Pending' && (
                        <>
                          <button onClick={() => handleStatusChange(vol._id, 'Approved')} className="p-2 text-green-600 hover:bg-green-50 rounded" title="Approve">
                             <CheckCircle size={18} />
                          </button>
                          <button onClick={() => handleStatusChange(vol._id, 'Rejected')} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Reject">
                             <XCircle size={18} />
                          </button>
                        </>
                    )}
                    {/* Ab window.confirm hat gaya hai, aur direct State change hoga modal kholne ke liye */}
                    <button onClick={() => setVolunteerToDelete(vol._id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded ml-2 transition-colors" title="Delete">
                        <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
              
              {volunteers.length === 0 && (
                  <tr>
                      <td colSpan={5} className="text-center py-8 text-slate-500">No applications received yet.</td>
                  </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminVolunteerList;